/**
 * Structured data for the Ova Balance bundle pages.
 *
 * Each bundle is emitted as a first-class Product with a stable @id, its own
 * SKU, a live Offer and `isRelatedTo` links to its component products. Price,
 * currency and availability are always passed in from the resolved live bundle
 * data — never hardcoded here.
 */
import { merchantOfferExtras } from "@/lib/seo/offer-schema";
import {
  ORGANIZATION_ID,
  SITE,
  bundleProductId,
  bundleRelatedProducts,
  bundleUrl,
  type EcosystemBundle,
} from "@/lib/ovaEcosystem";

export interface BundleSchemaInput {
  bundle: EcosystemBundle;
  /** Live bundle price in rupees. */
  price: number;
  currency?: string;
  availability: "InStock" | "OutOfStock" | "PreOrder";
  availabilityStarts?: string | null;
  /** Absolute https image URL that is actually shown on the page. */
  image?: string | null;
}

export function buildBundleProductSchema(input: BundleSchemaInput) {
  const url = bundleUrl(input.bundle);
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "@id": bundleProductId(input.bundle),
    name: input.bundle.name,
    url,
    description: input.bundle.summary,
    ...(input.image ? { image: [input.image] } : {}),
    sku: input.bundle.sku,
    brand: { "@type": "Brand", name: "TruWe" },
    category: "Dietary Supplement Bundle",
    isRelatedTo: bundleRelatedProducts(input.bundle),
    ...(input.price > 0
      ? {
          offers: {
            "@type": "Offer",
            url,
            priceCurrency: input.currency || "INR",
            price: String(Math.round(input.price)),
            availability: `https://schema.org/${input.availability}`,
            ...(input.availability === "PreOrder" && input.availabilityStarts
              ? { availabilityStarts: input.availabilityStarts }
              : {}),
            itemCondition: "https://schema.org/NewCondition",
            seller: { "@id": ORGANIZATION_ID },
            ...merchantOfferExtras(),
          },
        }
      : {}),
  };
}

export function buildBundleBreadcrumbSchema(bundle: EcosystemBundle) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
      { "@type": "ListItem", position: 2, name: "Shop", item: `${SITE}/shop` },
      {
        "@type": "ListItem",
        position: 3,
        name: "PCOS Nutrition",
        item: `${SITE}/collections/pcos-nutrition`,
      },
      {
        "@type": "ListItem",
        position: 4,
        name: bundle.breadcrumbLabel,
        item: bundleUrl(bundle),
      },
    ],
  };
}
